import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppState, Linking } from 'react-native';
import { getAccessToken } from '../services/client';
import {
  acceptVoiceCall,
  endVoiceCall,
  fetchIncomingVoiceCall,
  fetchVoiceCall,
  fetchVoiceEligibility,
  refreshVoiceToken,
  rejectVoiceCall,
  prepareVoiceDial,
  startVoiceCall,
  voiceErrorCode,
  type VoiceCall,
  type VoiceEligibility,
  type VoiceGrant,
  type VoiceSubjectType,
} from '../services/voice';
import { useAsync, type Async } from '../hooks/useAsync';
import { microphoneCopy, useMicrophonePermission } from './microphone';
import { ensureCallEnded } from './ensure-ended';
import { useCallScreen } from './useCallScreen';
import { dismissIncomingCall } from './incoming-call';
import { drainQueuedDecline, registerPushToken, subscribeVoicePush } from './push';
import {
  resolveTransport,
  type VoiceTransport,
  type VoiceTransportState,
} from './transport';
import {
  endedCopy,
  isLivePhase,
  isTerminalStatus,
  unreachableCopy,
  type CallPhase,
  type EndedCopy,
} from './types';

/** See `startCall`. True: the Call button opens the phone's dialler. */
const DIAL_FROM_PHONE = true;

/**
 * The whole of a voice call, in one place above the app.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A PROVIDER AND NOT A SCREEN
 * ---------------------------------------------------------------------------
 * This app has no navigator. `App.tsx` is a four-stage machine and, once it
 * reaches `home`, `HomeScreen` owns every other screen as a boolean and
 * returns whichever one is true from a chain of early guards. There is no
 * stack to push a call screen onto, and adding a branch to that chain would
 * mean a call could only be shown from Home - not from a booking, a sheet, or
 * the cart, and not at all during onboarding.
 *
 * So the call lives above all of it. This provider is mounted in `App.tsx`
 * inside `SafeAreaProvider`, and the two overlays it drives are React Native
 * `Modal`s, which float over the whole tree regardless of which branch
 * `HomeScreen` happened to return. Nothing in `HomeScreen`'s guard chain, and
 * nothing in its back-button `unwind`, needed changing.
 *
 * ---------------------------------------------------------------------------
 * THE ONE RULE THIS FILE IS BUILT AROUND
 * ---------------------------------------------------------------------------
 * **Call state comes from the server, never from a local guess.** The vendor's
 * checklist puts it plainly - "show call state from the webhook events, not
 * from local guesses" - and every awkward-looking decision below follows from
 * it:
 *
 *   · a call is `connected` because the server said `ACCEPTED`, never because
 *     a socket opened. A device that joins the room and immediately drops is
 *     not an answered call, and drawing one would bill nobody's attention for
 *     a conversation that never happened.
 *   · there is NO 45-second ring timer in this file. The server owns the ring
 *     timeout and marks the call `MISSED` itself. A second timer here would
 *     drift, and the two would disagree in front of the customer.
 *   · `ALREADY_RINGING` is not an error. It is a double tap, or it is glare -
 *     both sides dialling at once - and `role` is what tells them apart.
 *   · `/end` is idempotent, so a failure from it is never shown. Both apps
 *     racing to report the same hang-up is the ordinary case.
 *
 * The poll is what makes all of that true. Two seconds while a call is live,
 * five seconds for an incoming one while the app is idle and foregrounded.
 */

/** How often a live call is re-read from the server. */
const STATUS_POLL_MS = 2000;

/** How often an idle, foregrounded app asks whether anyone is ringing it. */
const INCOMING_POLL_MS = 5000;

/**
 * How long the "how it ended" card stays up before clearing itself.
 *
 * Not a call timer - the call is already over by the time this starts. It
 * exists so a modal reporting a missed call does not sit over the app until
 * somebody thinks to dismiss it.
 */
const ENDED_LINGER_MS = 4000;

/** Reconnects attempted with a fresh token before the call is given up on. */
const MAX_RECONNECTS = 3;

export type VoiceCallContextValue = {
  phase: CallPhase;
  /** The call the phase is about, as the server last described it. */
  call: VoiceCall | null;
  /** Where the audio engine is. Never the same thing as the call's status. */
  transportState: VoiceTransportState;
  /** False until the Synquic Voice SDK is installed - see `transport.ts`. */
  engineAvailable: boolean;
  muted: boolean;
  speaker: boolean;
  /** Something to say inline, on the surface that is already open. */
  notice: string | null;
  /** How the last call ended. Non-null exactly while `phase` is `ended`. */
  ended: EndedCopy | null;

  /**
   * Places a call.
   *
   * `counterpartName` is only ever used for copy, and only on the one failure
   * that needs it: a call the server could not even ring has to say who it
   * could not reach, and by then there is no call object to read a name from.
   * Callers that have already fetched eligibility pass the name they got;
   * everything else omits it and gets a pronoun.
   */
  startCall: (
    subjectType: VoiceSubjectType,
    subjectId: string,
    counterpartName?: string | null,
  ) => void;
  accept: () => void;
  reject: () => void;
  end: () => void;
  /**
   * Android has stopped drawing the microphone dialog for this process.
   *
   * The overlays read it to swap "allow it when asked" - which will never
   * happen again - for a control that opens system settings. Always false on
   * iOS, which has no such state.
   */
  micBlocked: boolean;
  /** Clears the ended card. Does nothing while a call is live. */
  dismiss: () => void;
  toggleMuted: () => void;
  toggleSpeaker: () => void;
};

const VoiceCallContext = createContext<VoiceCallContextValue | null>(null);

/**
 * The call, for anything that needs to start or draw one.
 *
 * Throws outside the provider rather than returning null, because every caller
 * would have to handle that null identically and none of them can do anything
 * useful with it. The provider is mounted at the root; a component that cannot
 * see it is in the wrong tree.
 */
export const useVoiceCall = (): VoiceCallContextValue => {
  const value = useContext(VoiceCallContext);
  if (!value) {
    throw new Error('useVoiceCall must be used inside <VoiceCallProvider>');
  }
  return value;
};

/**
 * The call, for a host that merely routes a press to it.
 *
 * Returns null outside the provider instead of throwing, and the distinction
 * from `useVoiceCall` is worth keeping: a component whose whole purpose is the
 * call - either overlay, `CallButton` - is broken without a provider and
 * should say so loudly. A screen that only forwards a tap is not, and
 * `HomeScreen` in particular is rendered on its own outside `App` (the test
 * suite does exactly that), where insisting on a call context would fail a
 * screen that has nothing to do with calling.
 */
export const useVoiceCallOptional = (): VoiceCallContextValue | null =>
  useContext(VoiceCallContext);

/**
 * Whether this customer may call the other party on this subject.
 *
 * Deliberately usable WITHOUT the provider - it reads the API and nothing
 * else, so a screen rendered in a test without a call context still draws its
 * button, merely disabled. `useContext` rather than `useVoiceCall` for exactly
 * that reason.
 *
 * Re-asked whenever a call ends, because one of the reasons it can be refused
 * is `YOU_ARE_ON_A_CALL` - a stale "no" would leave the button dead after the
 * customer hung up.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS A `revision`
 * ---------------------------------------------------------------------------
 * `subjectId` alone is not enough, and the gap was a real one. A booking's id
 * does not change for the whole life of the booking, but the answer to "can
 * you call anyone about it" does: `NOT_YET_ASSIGNED` becomes yes the moment a
 * professional is assigned. Keyed on the id alone, a customer who opened the
 * booking before assignment and pulled to refresh until somebody appeared was
 * left looking at a dark button captioned "Calling opens once a professional
 * is assigned" until they closed the screen and opened it again.
 *
 * So the caller passes whatever it already knows changes underneath - a stage,
 * a status, a version counter - and this refetches when it moves. Optional,
 * because a subject whose id genuinely is the whole story (a live ride) has
 * nothing to pass.
 */
export const useVoiceEligibility = (
  subjectType: VoiceSubjectType,
  subjectId: string | null | undefined,
  revision?: string | number | null,
): Async<VoiceEligibility> => {
  const voice = useContext(VoiceCallContext);
  const idle = voice ? voice.phase === 'idle' : true;

  /* `getAccessToken()` is not reactive, and does not need to be: signing in
     re-renders the tree from `HomeScreen`, so this is re-read at that point.
     Every screen that can reach a call button is behind a session anyway. */
  const signedIn = !!getAccessToken();

  return useAsync<VoiceEligibility>(
    () => fetchVoiceEligibility(subjectType, subjectId as string),
    [subjectType, subjectId, idle, revision],
    { enabled: !!subjectId && signedIn },
  );
};

export function VoiceCallProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<CallPhase>('idle');
  const [call, setCall] = useState<VoiceCall | null>(null);
  const [transportState, setTransportState] =
    useState<VoiceTransportState>('idle');
  const [muted, setMuted] = useState(false);
  const [speaker, setSpeaker] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [ended, setEnded] = useState<EndedCopy | null>(null);

  /*
   * Blank the screen at an ear, and keep it awake the rest of the time.
   *
   * Here rather than in either overlay, for two reasons. The native side holds
   * one flag per process, and this provider is mounted exactly once, at the
   * root - a second instance would fight it. And screen control belongs to the
   * state of the call rather than to a mounted component: an overlay that goes
   * away mid-call must not take the lock with it.
   */
  useCallScreen(phase, speaker);

  /**
   * The audio engine, resolved once.
   *
   * `useMemo` with no dependencies rather than a module-level const so a
   * reload in development picks up an SDK that has just been installed, and so
   * importing this file never touches native code.
   */
  const transport: VoiceTransport = useMemo(() => resolveTransport(), []);

  /**
   * Whether this handset has an account on it yet.
   *
   * Only ever set, never cleared: it arms the microphone prompt below, and a
   * sign-out is not a reason to un-ask a question that has already been
   * answered. `getAccessToken()` is not reactive, so this is raised from the
   * two places that already read it on a schedule - the foreground drain and
   * the incoming poll - which between them cover mount, every return to the
   * foreground, and signing in mid-session.
   */
  const [hasSession, setHasSession] = useState<boolean>(() => !!getAccessToken());

  /**
   * The microphone: OBSERVED by mounting this, asked for by the call paths.
   *
   * ---------------------------------------------------------------------------
   * WHY THE ASK LIVES HERE AND NOT IN `startCall` / `acceptCall`
   * ---------------------------------------------------------------------------
   * It used to live in both of those and in nothing else, which meant the only
   * two moments this app ever asked for a microphone were the two moments the
   * vendor's checklist names as the wrong ones (§8: "request microphone
   * permission before the first call, not during"). `acceptCall` was the worse
   * of the two by a distance: the handset is ringing, the overlay is up, and a
   * system dialog lands on top of both. People dismiss that box to get at the
   * ringing phone, and on Android the fastest dismissal is Deny - twice, and
   * it is `never_ask_again`, which nothing in this app can undo.
   *
   * Mounting the hook here used to BE the ask, and that overcorrected. This
   * provider wraps the whole tree, so the prompt landed on whatever screen
   * came first after sign-in - the reported symptom was a customer adding a
   * car wash to their basket being asked to record audio, with nothing on
   * screen to explain why. Out of context, the reflex answer is Deny, and two
   * denials are `never_ask_again`.
   *
   * So mounting now only OBSERVES (a `check`, which draws nothing), and the
   * prompt is raised by `requestMicrophone()` from `startCall` and
   * `acceptCall` - the two moments the customer has just asked for a call.
   * §8's "before the first call, not during" still holds: both ask before
   * dialling or connecting, not over a live one.
   *
   * Still armed on `hasSession`, which now only governs whether the check
   * runs: a signed-out handset can neither place a call nor be rung for one.
   */
  const { request: requestMicrophone, blocked: micBlocked } =
    useMicrophonePermission(hasSession);

  /*
   * Mirrors of the two pieces of state the timers and push handlers read.
   *
   * Both are registered once and would otherwise close over the first render's
   * values - an interval checking a `phase` that is permanently 'idle' would
   * poll for incoming calls straight through a conversation.
   */
  const phaseRef = useRef<CallPhase>(phase);
  phaseRef.current = phase;
  const callRef = useRef<VoiceCall | null>(call);
  callRef.current = call;

  const reconnects = useRef(0);

  // --------------------------------------------------------------- the engine

  useEffect(
    () => transport.subscribe(next => setTransportState(next)),
    [transport],
  );

  /**
   * A problem that leaves the call up, said out loud.
   *
   * ---------------------------------------------------------------------------
   * THE CALL THAT LOOKED PERFECT AND CARRIED NOTHING
   * ---------------------------------------------------------------------------
   * The only member so far is a microphone that failed to publish, and it is
   * an iPhone story. iOS has no way to ask for the microphone in advance - the
   * prompt fires on first capture, which is after the call is up - so
   * `ensureMicrophone` reports `granted` there and nothing checks. A customer
   * who taps "Don't Allow" then gets a call that connects, a screen that shows
   * a live call with a timer running, and a professional hearing silence.
   *
   * `notice` rather than a failure, because that is the truth: they can still
   * HEAR the call, and dropping it would take away the half that works. It is
   * cleared by `finish` along with every other notice when the call ends.
   */
  useEffect(
    () =>
      transport.subscribeProblems(problem => {
        if (problem === 'microphone-unavailable') {
          setNotice(
            'They cannot hear you - Homingo has no access to your microphone. ' +
              'Turn it on in Settings to speak.',
          );
        }
      }),
    [transport],
  );
  const connect = useCallback(
    async (grant: VoiceGrant, callId: string): Promise<void> => {
      reconnects.current = 0;
      await transport.connect({
        token: grant.token,
        voiceServerUrl: grant.voiceServerUrl,
        callId,
      });
    },
    [transport],
  );

  /**
   * Tears the call down locally and says how it went.
   *
   * Everything that finishes a call lands here - a terminal status from the
   * poll, a hang-up, a decline, a push saying the other side gave up - so the
   * cleanup happens exactly once and in one order.
   */
  const finish = useCallback(
    (copy: EndedCopy, callId?: string) => {
      transport.disconnect().catch(() => undefined);
      dismissIncomingCall(callId ?? callRef.current?.callId).catch(
        () => undefined,
      );
      setMuted(false);
      setSpeaker(false);
      setNotice(null);
      setEnded(copy);
      setPhase('ended');
    },
    [transport],
  );

  /**
   * Takes the server's word for what the call is doing.
   *
   * The only function that moves the phase forward on the strength of a
   * status, and therefore the only place the server's vocabulary is turned
   * into this app's. Everything else either asks the server or reports what it
   * has already said.
   */
  const applyServerCall = useCallback(
    (next: VoiceCall) => {
      setCall(next);

      if (isTerminalStatus(next.status)) {
        finish(
          endedCopy(next.status, next.role, next.durationSeconds),
          next.callId,
        );
        return;
      }

      const current = phaseRef.current;

      if (next.status === 'ACCEPTED') {
        /* Answered. Whether the audio has caught up is a separate question,
           answered by the transport effect below - this only promotes out of
           the ringing states. */
        if (
          current === 'dialling' ||
          current === 'ringing-out' ||
          current === 'incoming'
        ) {
          setPhase('connecting');
        }
        return;
      }

      /* RINGING. Only meaningful as a discovery - an idle app learning that
         somebody is calling it. A call already on screen is left where it is. */
      if (current === 'idle' || current === 'ended') {
        setNotice(null);
        setEnded(null);
        setPhase('incoming');
      }
    },
    [finish],
  );

  /*
   * Audio arriving is what turns "answered" into "connected".
   *
   * Split from the status above deliberately: the server decides the call is
   * answered, the engine decides the sound is flowing, and the customer is
   * told the second one only once both are true.
   */
  useEffect(() => {
    if (phase === 'connecting' && transportState === 'connected') {
      setPhase('connected');
    }
    /*
     * Keyed on BOTH, and it has to be. The caller joins the room while the
     * call is still ringing, so by the time the server confirms the answer the
     * engine has long since reported `connected` and will not say it again -
     * an effect watching the transport alone would leave that call reading
     * "Connecting…" for its whole duration.
     */
  }, [phase, transportState]);

  /*
   * A dropped connection, rejoined with a fresh credential.
   *
   * The per-call token is short-lived by design, so a handset that loses the
   * network for a minute cannot simply replay the one it joined with - the
   * vendor's guide says to ask for another, and this is that. Bounded, because
   * a room that refuses three fresh tokens is not coming back and retrying for
   * ever would keep a dead call on screen.
   *
   * Skipped entirely when there is no engine: `failed` is then a permanent,
   * expected state and the overlay is already saying so.
   */
  useEffect(() => {
    if (!transport.isAvailable || transportState !== 'failed') {
      return;
    }
    const current = callRef.current;
    if (!current || current.status !== 'ACCEPTED') {
      return;
    }
    if (reconnects.current >= MAX_RECONNECTS) {
      return;
    }

    reconnects.current += 1;
    let cancelled = false;

    refreshVoiceToken(current.callId)
      .then(grant => {
        if (cancelled) {
          return undefined;
        }
        return transport.connect({
          token: grant.token,
          voiceServerUrl: grant.voiceServerUrl,
          callId: current.callId,
        });
      })
      .catch(() => {
        /* The next poll will report the call as over if the server has given
           up on it too. Nothing to show for a single failed rejoin. */
      });

    return () => {
      cancelled = true;
    };
  }, [transport, transportState]);

  // ---------------------------------------------------------------- the calls

  const startCall = useCallback(
    (
      subjectType: VoiceSubjectType,
      subjectId: string,
      counterpartName?: string | null,
    ) => {
      if (isLivePhase(phaseRef.current)) {
        return;
      }

      /*
       * Calls go out on the phone's own dialler, to our business number.
       *
       * The in-app call below is kept, not deleted - flip this to put it back.
       * It needs no SIM and costs no phone minutes, but a customer on a weak
       * data connection, the case this change is for, could not use it.
       */
      if (DIAL_FROM_PHONE) {
        void (async () => {
          try {
            const { phone } = await prepareVoiceDial(subjectType, subjectId);
            await Linking.openURL(`tel:${phone}`);
          } catch {
            setEnded({
              title: 'Could not start the call',
              detail: 'Please try again in a moment.',
            });
            setPhase('ended');
          }
        })();
        return;
      }

      void (async () => {
        setNotice(null);
        setEnded(null);
        setPhase('requesting-permission');

        /* THE ask, and the right moment for it: somebody has just pressed
           Call, so "Homingo needs your microphone" needs no explaining. The
           provider no longer prompts on mount - see `microphone` above - it
           only observes, which is what kept this prompt off the catalogue. */
        const mic = await requestMicrophone();
        if (mic !== 'granted') {
          setEnded({ title: 'Microphone needed', detail: microphoneCopy(mic) });
          setPhase('ended');
          return;
        }

        setPhase('dialling');

        try {
          const started = await startVoiceCall(subjectType, subjectId);

          /* A provisional call, so the overlay has a name to draw immediately.
             The poll replaces it with the server's own copy within two
             seconds; nothing here is treated as fact. */
          const provisional: VoiceCall = {
            callId: started.callId,
            status: 'RINGING',
            subjectType,
            subjectId,
            role: started.role,
            counterpart: started.counterpart,
            ringTimeoutSeconds: started.ringTimeoutSeconds,
            ringingAt: null,
            acceptedAt: null,
            endedAt: null,
            durationSeconds: null,
            failureReason: null,
          };
          setCall(provisional);

          /*
           * Glare: we dialled, and so did they, and they got there first.
           *
           * `ALREADY_RINGING` with `role: 'callee'` means this handset is the
           * one being rung. The right response is not an error - it is to stop
           * dialling and start answering.
           */
          if (started.role === 'callee') {
            /*
             * Nothing is connected here, and nothing may be.
             *
             * `token` is null on this branch by design - the token `POST
             * /calls` mints is scoped to the CALLER, and this device is not
             * one. Its own credential comes later, from `/accept` (or
             * `/token`, see `acceptCall`), and only once somebody has actually
             * answered. Trying to reuse the caller's field would be joining
             * the room as the wrong participant.
             */
            setPhase('incoming');
            fetchVoiceCall(started.callId).then(applyServerCall).catch(() => undefined);
            return;
          }

          setPhase('ringing-out');

          /*
           * The caller joins the room while it rings, which is what makes the
           * first second of the answered call audible rather than silent. The
           * phase stays `ringing-out` regardless - joining a room is not the
           * same as being answered, and only the server can say that.
           *
           * -------------------------------------------------------------------
           * A START REPLY WITH NO TOKEN IS NOT A CALL THAT NEEDS NONE
           * -------------------------------------------------------------------
           * `POST /calls` normally mints the caller's credential and returns it
           * here. It does not on the ADOPT path: `ALREADY_RINGING` means a call
           * on this subject was already up - a double tap, or a first attempt
           * that timed out on a slow link and was pressed again - and the
           * server hands back `token: null, voiceServerUrl: null` for BOTH
           * roles, because it is describing a call it did not just create.
           *
           * This was once `if (started.token && ...) connect(...)` with no else,
           * and the consequence was as bad as it was quiet: the transport was
           * never asked to do anything, so it sat at `idle` - not `failed` -
           * and the reconnect effect that would have fetched a fresh token only
           * fires on `failed`. Nothing else ever asked for one. The phase walked
           * ringing-out → connecting when the other side answered and stayed
           * there, in silence, for the whole call.
           *
           * So a missing token is fetched from `/token`, the route that exists
           * for exactly this: a correctly-scoped credential for a call that is
           * already up. The callee half of the same adopt reply is covered
           * already - it returned above, and `acceptCall` has the same `/token`
           * fallback for the same reason. A callee must not join before it has
           * accepted (§7: accepting is explicit), which is why that half asks
           * there and not here.
           *
           * Fired rather than awaited, deliberately. The server has already said
           * this call is ringing; holding the phase behind a round trip would
           * put a failed token fetch onto the "Call failed" path below for a
           * call that is at that moment ringing the other handset.
           */
          void (async () => {
            try {
              const grant =
                started.token && started.voiceServerUrl
                  ? {
                      token: started.token,
                      voiceServerUrl: started.voiceServerUrl,
                    }
                  : await refreshVoiceToken(started.callId);
              await connect(grant, started.callId);
            } catch {
              /* No audio yet, and nothing to say about it: the call is still
                 ringing. If the room stays unreachable the transport reports
                 `failed` and the reconnect effect above takes it from there. */
            }
          })();
        } catch (thrown) {
          const code = voiceErrorCode(thrown);
          const message =
            thrown instanceof Error ? thrown.message : 'The call could not be placed.';

          if (code === 'YOU_ARE_ON_A_CALL') {
            setEnded({
              title: 'Already on a call',
              detail: 'Finish that call before starting another one.',
            });
          } else if (code === 'VOICE_NOT_CONFIGURED') {
            setEnded({
              title: 'Calling unavailable',
              detail: 'In-app calling is not switched on yet.',
            });
          } else if (code === 'SESSION_NOT_ACTIVE') {
            setEnded({
              title: 'Calling has closed',
              detail: 'This job is over, so calling is no longer available.',
            });
          } else if (code === 'CALLEE_UNREACHABLE') {
            /* Their phone was never rung at all - no usable push token - so
               the server ended the call rather than faking ringback. Named,
               and never worded as "no answer": see `unreachableCopy`. Nothing
               to press, because a retry now would fail identically. */
            setEnded(unreachableCopy(counterpartName));
          } else if (code === 'NOT_A_PARTICIPANT') {
            /* This customer is not on that subject, or the call moved on
               underneath them. Not their doing and not fixable by them, so it
               gets the plain sentence and closes rather than an explanation of
               a state they did not cause. */
            setEnded({
              title: 'Call unavailable',
              detail: 'That call is no longer available.',
            });
          } else {
            setEnded({ title: 'Call failed', detail: message });
          }

          setCall(null);
          setPhase('ended');
        }
      })();
    },
    [applyServerCall, connect, requestMicrophone],
  );

  /**
   * Answers. Takes the id explicitly so the notification's Accept button can
   * answer a call this React tree has not caught up with yet.
   *
   * Reached ONLY by the Accept button on the notification and by the overlay's
   * own Answer control. A tap on the BODY of a card goes to `openIncomingCall`
   * below - see the note there, and on `VoicePushHandlers` in `push.ts`.
   */
  const acceptCall = useCallback(
    (callId: string) => {
      void (async () => {
        dismissIncomingCall(callId).catch(() => undefined);

        /* Asked here only if it has never been answered - normally a `check`
           that returns `granted` and draws nothing. A dialog on top of a
           ringing phone is the failure `microphone.ts` is written to avoid,
           so a refusal returns to the ringing state with a notice and an
           "Open Settings" control rather than trapping the customer on a
           screen whose only other option is to hang up. */
        setPhase('requesting-permission');
        const mic = await requestMicrophone();
        if (mic !== 'granted') {
          /* Back to the ringing state rather than out of it - the call is
             still up, and the customer may yet grant the permission. */
          setNotice(microphoneCopy(mic));
          setPhase('incoming');
          return;
        }

        setNotice(null);
        setPhase('connecting');

        try {
          /*
           * Answering and getting a credential are one call, but they are two
           * facts, and only the first is guaranteed. `/accept` is what marks
           * the call ACCEPTED; the token it returns is this device's own -
           * never the one from `POST /calls`, which is scoped to the caller
           * and comes back null for a callee on purpose.
           *
           * If it arrives without a usable one - which is the shape a glare
           * hand-off can leave behind - the credential is fetched from
           * `/token`, the route that exists for exactly this: a fresh,
           * correctly-scoped token for a call that is already up.
           */
          const accepted = await acceptVoiceCall(callId);
          const grant =
            accepted?.token && accepted?.voiceServerUrl
              ? accepted
              : await refreshVoiceToken(callId);

          await connect(grant, callId);

          /* Fetched rather than assumed. `/accept` returns a credential, not a
             call, and the phase must not claim a status nobody has confirmed. */
          fetchVoiceCall(callId).then(applyServerCall).catch(() => undefined);
        } catch (thrown) {
          const code = voiceErrorCode(thrown);
          if (code === 'CALL_NO_LONGER_RINGING' || code === 'NOT_A_PARTICIPANT') {
            /* They hung up, or it rang out, while the accept was in flight -
               or this screen is acting on a call that is no longer ours.
               Either way there is nothing to answer: close the UI and stop
               ringing rather than sitting on a spinner. */
            finish({ title: 'Call ended', detail: null }, callId);
            return;
          }
          const message =
            thrown instanceof Error ? thrown.message : 'The call could not be answered.';
          finish({ title: 'Call failed', detail: message }, callId);
        }
      })();
    },
    [applyServerCall, connect, finish, requestMicrophone],
  );

  /**
   * Puts an incoming call on screen. Answers nothing.
   *
   * ---------------------------------------------------------------------------
   * THE OTHER HALF OF A NOTIFICATION PRESS
   * ---------------------------------------------------------------------------
   * Tapping the body of a ringing card means "show me this", and it used to be
   * routed to `acceptCall` - so a customer who tapped a notification to find
   * out who was calling was in a live call with an open microphone before the
   * app had finished drawing. Nobody agreed to that, and there is no undo.
   *
   * The tap lands here instead: read the call back from the server and let
   * `applyServerCall` raise the overlay, which has Answer and Decline on it and
   * the caller's name above them. Only `VOICE_ACTION_ACCEPT` reaches
   * `acceptCall`.
   *
   * Read rather than assumed, for the same reason `onIncoming` reads it: a
   * notification carries an id and a name, not a call, and the call may well
   * have rung out while the handset was in a pocket. The status decides what is
   * drawn - including the ended card, when the answer is that it is over.
   */
  const openIncomingCall = useCallback(
    (callId: string) => {
      const current = callRef.current;
      /* A different call is already on screen. Two cannot both be up, and the
         one being looked at wins over one a stale notification names. */
      if (isLivePhase(phaseRef.current) && current && current.callId !== callId) {
        return;
      }
      fetchVoiceCall(callId).then(applyServerCall).catch(() => undefined);
    },
    [applyServerCall],
  );

  const rejectCall = useCallback(
    (callId: string) => {
      void (async () => {
        dismissIncomingCall(callId).catch(() => undefined);
        setPhase('ending');

        let stale = false;
        try {
          await rejectVoiceCall(callId);
        } catch (thrown) {
          /*
           * Swallowed for the same reason `/end` is: the call has almost
           * certainly already gone, and telling somebody their decline failed
           * invites them to press it again at a call that is not there.
           *
           * One case is worth separating. `NOT_A_PARTICIPANT` means this
           * device was never the callee on that call, so "Declined" would be
           * claiming an act that did not happen - and somebody reading it
           * would believe they had turned away a call they never had.
           */
          stale = voiceErrorCode(thrown) === 'NOT_A_PARTICIPANT';
        }

        finish(
          stale
            ? { title: 'Call unavailable', detail: 'That call is no longer available.' }
            : { title: 'Declined', detail: null },
          callId,
        );
      })();
    },
    [finish],
  );

  const accept = useCallback(() => {
    const current = callRef.current;
    if (current) {
      acceptCall(current.callId);
    }
  }, [acceptCall]);

  const reject = useCallback(() => {
    const current = callRef.current;
    if (current) {
      rejectCall(current.callId);
    }
  }, [rejectCall]);

  const end = useCallback(() => {
    const current = callRef.current;
    if (!current) {
      return;
    }

    /*
     * The screen closes now; the server is told as soon as it can be heard.
     *
     * These used to be the same await, and a failure was swallowed on the
     * grounds that `/end` is idempotent and both ends race to call it. True of
     * a lost race, and false of a request that never arrived - see
     * `ensureCallEnded`. When it never arrived, the platform went on believing
     * this person was mid-conversation, and their next call attempt was
     * refused with "You are already on a call" about a call that had ended
     * minutes earlier.
     */
    setPhase('ending');

    void (async () => {
      const closed = await ensureCallEnded(() => endVoiceCall(current.callId));

      finish(
        closed
          ? endedCopy(closed.status, closed.role, closed.durationSeconds)
          : { title: 'Call ended', detail: null },
        current.callId,
      );
    })();
  }, [finish]);

  const dismiss = useCallback(() => {
    if (isLivePhase(phaseRef.current)) {
      return;
    }
    setEnded(null);
    setNotice(null);
    setCall(null);
    setPhase('idle');
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted(current => {
      const next = !current;
      transport.setMuted(next).catch(() => undefined);
      return next;
    });
  }, [transport]);

  const toggleSpeaker = useCallback(() => {
    setSpeaker(current => {
      const next = !current;
      transport.setSpeakerphone(next).catch(() => undefined);
      return next;
    });
  }, [transport]);

  // ----------------------------------------------------------------- the poll

  /*
   * The live call, re-read every two seconds.
   *
   * This is the mechanism behind the rule at the top of the file. Ringing
   * becomes answered here, answered becomes missed here, and the other side
   * hanging up is learned here - none of it inferred, all of it read back.
   * It stops on a terminal status because `applyServerCall` moves the phase
   * out of the live set, which is what this effect is keyed on.
   */
  const liveCallId = call?.callId ?? null;
  const live = isLivePhase(phase);

  useEffect(() => {
    if (!liveCallId || !live) {
      return;
    }

    let cancelled = false;
    const tick = () => {
      fetchVoiceCall(liveCallId)
        .then(next => {
          if (!cancelled) {
            applyServerCall(next);
          }
        })
        .catch(() => {
          /* A dropped poll is not a dropped call. The next tick tries again,
             and the customer is left looking at the last state that was true. */
        });
    };

    const id = setInterval(tick, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [applyServerCall, live, liveCallId]);

  /*
   * Is anybody ringing us? Asked every five seconds while there is nothing
   * else going on.
   *
   * The net beside push, and the only thing that rings an iPhone at all (see
   * `incoming-call.ts`). Three conditions, checked on every tick rather than
   * baked into the effect's dependencies, because all three change without
   * re-rendering this component: the app can be backgrounded, the customer can
   * sign out, and a call can start.
   */
  useEffect(() => {
    const tick = () => {
      if (
        phaseRef.current !== 'idle' ||
        AppState.currentState !== 'active' ||
        !getAccessToken()
      ) {
        return;
      }

      /* Open, signed in and idle - the same three facts the microphone prompt
         wants to be armed by, and the only signal this provider gets when
         somebody signs in mid-session without the app ever backgrounding. */
      setHasSession(true);

      fetchIncomingVoiceCall()
        .then(incoming => {
          if (incoming && phaseRef.current === 'idle') {
            applyServerCall(incoming);
          }
        })
        .catch(() => undefined);
    };

    const id = setInterval(tick, INCOMING_POLL_MS);
    return () => clearInterval(id);
  }, [applyServerCall]);

  /*
   * The ended card clears itself.
   *
   * Four seconds is long enough to read "No answer" and short enough that a
   * phone put back in a pocket does not come out with a modal still on it.
   */
  useEffect(() => {
    if (phase !== 'ended') {
      return;
    }
    const id = setTimeout(() => dismiss(), ENDED_LINGER_MS);
    return () => clearTimeout(id);
  }, [dismiss, phase]);

  // ----------------------------------------------------------------- the push

  useEffect(() => {
    let off: (() => void) | null = null;

    try {
      off = subscribeVoicePush({
        onIncoming: push => {
          if (!push.callId) {
            return;
          }

          /*
           * `ended` counts as free, and leaving it out was a real gap.
           *
           * The ended card lingers for four seconds (`ENDED_LINGER_MS`), and a
           * push arriving inside that window used to be dropped here - but
           * `push.ts` had already drawn the notification, so the handset rang
           * with no overlay behind it until the five-second incoming poll
           * happened to catch it. Up to nine seconds of a phone ringing for a
           * call this app was refusing to show.
           *
           * `applyServerCall` already treats `ended` as a state a new call may
           * arrive into - it clears the card and moves to `incoming` - so this
           * only ever had to stop saying no.
           */
          const current = phaseRef.current;
          if (current !== 'idle' && current !== 'ended') {
            return;
          }

          /* The push carries a name and an id, not a call. Read the real one
             rather than draw a card out of a notification payload. */
          fetchVoiceCall(push.callId).then(applyServerCall).catch(() => undefined);
        },

        onEnded: endedCallId => {
          const current = callRef.current;
          if (!current || (endedCallId && endedCallId !== current.callId)) {
            return;
          }
          /* Read back rather than trusted: `missed` and `rejected` need
             different words, and the push says which but the call says why. */
          fetchVoiceCall(current.callId)
            .then(applyServerCall)
            .catch(() => finish({ title: 'Call ended', detail: null }));
        },

        /* Accept, and only Accept - see `VoicePushHandlers` in `push.ts`. */
        onAcceptPressed: acceptCall,
        onDeclinePressed: rejectCall,
        onOpenPressed: openIncomingCall,
      });
    } catch {
      /*
       * Firebase is not configured on this build - no `google-services.json`,
       * or no Play Services on the handset. Deliberately survivable: without
       * push the app simply falls back to the five-second incoming poll, which
       * covers every case where somebody is actually holding the phone.
       */
      console.warn(
        '[voice] push could not be wired up. Incoming calls will only be ' +
          'found by the in-app poll while the app is open.',
      );
    }

    return () => off?.();
  }, [acceptCall, applyServerCall, finish, openIncomingCall, rejectCall]);

  /*
   * A Decline whose reject never reached the server, sent now that it can.
   *
   * Only two things produce these: a killed app, which has no session to send
   * with, and a handset that had no network at the moment. `index.js` sends its
   * own whenever it can, so this is normally a no-op - and re-running it on
   * every return to the foreground costs one read of disk.
   */
  useEffect(() => {
    const drain = () => {
      /* Also where the microphone prompt is armed. `getAccessToken()` is not
         reactive, and this already runs at exactly the moments worth re-reading
         it. React bails out on an unchanged value, so repeating it is free. */
      if (getAccessToken()) {
        setHasSession(true);
      }
      drainQueuedDecline(async id => {
        await rejectVoiceCall(id);
      }).catch(() => undefined);
      registerPushToken().catch(() => undefined);
    };

    drain();
    const subscription = AppState.addEventListener('change', next => {
      if (next === 'active') {
        drain();
      }
    });
    return () => subscription.remove();
  }, []);

  const value = useMemo<VoiceCallContextValue>(
    () => ({
      phase,
      call,
      transportState,
      engineAvailable: transport.isAvailable,
      muted,
      speaker,
      notice,
      ended,
      micBlocked,
      startCall,
      accept,
      reject,
      end,
      dismiss,
      toggleMuted,
      toggleSpeaker,
    }),
    [
      accept,
      call,
      dismiss,
      end,
      ended,
      micBlocked,
      muted,
      notice,
      phase,
      reject,
      speaker,
      startCall,
      toggleMuted,
      toggleSpeaker,
      transport.isAvailable,
      transportState,
    ],
  );

  return (
    <VoiceCallContext.Provider value={value}>
      {children}
    </VoiceCallContext.Provider>
  );
}

export default VoiceCallProvider;
